import { Box, Button, CircularProgress, IconButton, ImageList, ImageListItem, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useDispatch, useSelector } from 'react-redux';
import { removeImportedAttachment, uploadQuoteAttachments } from 'store/slices/marketplace';
import { fileTypeChecks } from 'utils/file-icon';
import { useRef } from 'react';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const AttachmentImagePreview = ({ imageUrl = '', altName = 'attachment-image', status = 'pending' }) => {
  return (
    <img
      src={imageUrl}
      alt={altName}
      loading="lazy"
      style={{
        borderRadius: '8px',
        height: '67px',
        border: status === 'rejected' ? '1px solid red' : '1px solid #e7e8ea',
      }}
    />
  );
};

export const AttachmentFileIconPreview = ({ status = '', contentType = '' }) => {
  return (
    <Box
      width="67px"
      height="67px"
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        border: status === 'rejected' ? '1px solid red' : '1px solid #e7e8ea',
        borderRadius: '8px',
      }}
    >
      {fileTypeChecks.isFilePdf({ content_type: contentType }) ? (
        <PictureAsPdfIcon
          fontSize="large"
          sx={{ color: '#2b63a6' }}
        />
      ) : (
        <DescriptionOutlinedIcon
          fontSize="large"
          sx={{ color: '#2b63a6' }}
        />
      )}
    </Box>
  );
};

export const AttachmentRemoveButton = ({ onClick = () => {} }) => {
  return (
    <Tooltip
      title="Remove"
      placement="top"
      arrow
    >
      <IconButton
        aria-label="delete"
        size="small"
        sx={{ position: 'absolute', right: 0 }}
        onClick={onClick}
      >
        <CancelIcon
          font="inherit"
          sx={{
            color: '#595959',
          }}
        />
      </IconButton>
    </Tooltip>
  );
};

export const QuoteAttachment = ({ quoteId, itemId }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const importedAttachments = useSelector((state) => state.marketplace.importedAttachments);

  const handleFileChange = (event) => {
    event.stopPropagation();
    const files = event.target.files;
    if (files.length > 0) {
      dispatch(uploadQuoteAttachments({ quoteId, files, itemId }));
    }
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (id) => {
    dispatch(removeImportedAttachment({ id }));
  };

  return (
    <Stack spacing={1}>
      <ImageList
        cols={4}
        gap={6}
        rowHeight={'auto'}
      >
        {importedAttachments.map((file) => (
          <ImageListItem
            key={file.id}
            sx={{ position: 'relative' }}
          >
            <Tooltip
              title={`${file.name}`}
              placement="top"
              arrow
            >
              <>
                {fileTypeChecks.isFileImage(file) ? (
                  <AttachmentImagePreview
                    imageUrl={file.fileUrl}
                    altName={file.name}
                    status={file.status}
                  />
                ) : (
                  <AttachmentFileIconPreview
                    status={file.status}
                    contentType={file.content_type}
                  />
                )}
              </>
            </Tooltip>
            {file.status === 'pending' && <CircularProgress sx={{ position: 'absolute', top: '13px', left: '13px' }} />}
            {(file.status === 'fulfilled' || file.status === 'rejected') && (
              <AttachmentRemoveButton onClick={() => handleRemoveAttachment(file.id)} />
            )}
          </ImageListItem>
        ))}
      </ImageList>
      <Stack
        direction="row"
        justifyContent="end"
      >
        <Button
          component="label"
          role={undefined}
          startIcon={<AttachmentOutlinedIcon />}
        >
          <VisuallyHiddenInput
            type="file"
            onChange={handleFileChange}
            multiple
            ref={inputRef}
          />
          Attach File
        </Button>
      </Stack>
    </Stack>
  );
};
